import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getCourse, enrollInCourse } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import CoursePricing from '../components/CoursePricing';
import axios from 'axios';
import {
  Users, Clock, BookOpen, PlayCircle, Award, CheckCircle,
  Star, Share2, Heart, ArrowLeft, Play, IndianRupee, Zap,
  Target, Sparkles, GraduationCap, MessageCircle, Calendar,
  FileText, Download, Shield, TrendingUp, Palette, MonitorPlay
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseDetail() {
  const { courseId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [pricingTiers, setPricingTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  useEffect(() => {
    loadCourse();
    loadPricingTiers();
  }, [courseId]);

  const loadCourse = async () => {
    try {
      const response = await getCourse(courseId);
      setCourse(response.data);
    } catch (error) {
      console.error('Error loading course:', error);
      // Show error and navigate back
      toast.error('Course not found');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingTiers = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/pricing`);
      setPricingTiers(response.data.pricing_tiers);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  };

  const handleEnroll = async (enrollmentData) => {
    // Navigate directly to checkout page (supports guest checkout)
    const tier = enrollmentData?.tierType || 'instructor_led';
    const coupon = enrollmentData?.couponCode;
    navigate(`/checkout/${courseId}?tier=${tier}${coupon ? `&coupon=${coupon}` : ''}`);
  };

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const totalVideos = course?.modules?.reduce((acc, m) => acc + (m.videos?.length || 0), 0) || 0;
  const totalMinutes = course?.modules?.reduce((acc, m) => 
    acc + (m.videos?.reduce((a, v) => a + (v.duration_minutes || 0), 0) || 0), 0) || 0;

  // Get sample/preview videos from first module
  const previewVideos = course?.modules?.[0]?.videos?.slice(0, 2) || [];

  // Course highlights based on category
  const getHighlights = () => {
    const baseHighlights = [
      'Industry-standard tools & techniques',
      'Portfolio-ready projects',
      'Certificate on completion',
      'Lifetime access to course content',
      'Direct mentor support',
      'Community access'
    ];
    
    if (course?.skills?.length > 0) {
      return course.skills.map(skill => `Master ${skill}`).concat(baseHighlights.slice(2));
    }
    return baseHighlights;
  };

  // Career opportunities based on course
  const getCareerOpportunities = () => {
    const careers = {
      'Foundation': ['Junior Designer', 'Design Intern', 'Creative Assistant'],
      '3D & Animation': ['3D Artist', 'Character Animator', 'VFX Artist', 'Game Designer'],
      'Post Production': ['Video Editor', 'Colorist', 'Compositor', 'Motion Graphics Artist'],
      'Professional': ['Art Director', 'Creative Director', 'Lead Designer'],
      'Crash Courses': ['Junior Designer', 'Creative Assistant', 'Design Intern'],
      'Job Oriented Courses': ['3D Artist', 'VFX Artist', 'Video Editor', 'Graphic Designer', 'Motion Graphics Artist'],
      'Graduation Courses': ['Art Director', 'Creative Director', 'Lead Animator', 'Studio Head']
    };
    return careers[course?.category] || ['Creative Professional', 'Freelance Artist', 'Studio Artist'];
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <Skeleton className="h-[400px] rounded-2xl mb-8" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
            <Skeleton className="h-[300px] rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Course Not Found</h1>
          <Button onClick={() => navigate('/courses')}>Browse All Courses</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="course-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/courses')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Programs
        </Button>

        {/* Hero Section with Video Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-2xl overflow-hidden mb-8"
        >
          <div className="aspect-[21/9] relative">
            <img
              src={course?.thumbnail || 'https://images.unsplash.com/photo-1561070791-2526d30994b5'}
              alt={course?.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
            
            {/* Play Preview Button */}
            {previewVideos.length > 0 && (
              <button 
                onClick={() => setShowVideoPreview(true)}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center group hover:bg-white/30 transition-all"
              >
                <Play className="w-8 h-8 text-white fill-white ml-1" />
              </button>
            )}
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-primary/90">{course?.category}</Badge>
              <Badge variant="outline" className="border-emerald-500/50 text-emerald-500">
                <Sparkles className="w-3 h-3 mr-1" />
                {course?.is_published ? 'Enrolling Now' : 'Coming Soon'}
              </Badge>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              {course?.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm md:text-base text-muted-foreground">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 md:w-5 md:h-5" />
                {course?.enrolled_count || 0} students enrolled
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 md:w-5 md:h-5" />
                {course?.duration_weeks || Math.ceil((course?.duration_months || 3) * 4)} weeks
              </span>
              <span className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4 md:w-5 md:h-5" />
                {totalVideos} video lessons
              </span>
              <span className="flex items-center gap-2">
                <Star className="w-4 h-4 md:w-5 md:h-5 fill-yellow-500 text-yellow-500" />
                4.8 rating
              </span>
            </div>
          </div>
        </motion.div>

        {/* Mobile CTA - Sticky at bottom */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-border z-50">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-2xl font-bold flex items-center">
                <IndianRupee className="w-5 h-5" />
                {(course?.price || 0).toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-muted-foreground">One-time payment</p>
            </div>
            <Button
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 px-8"
              size="lg"
              onClick={handleEnroll}
              disabled={enrolling}
            >
              {enrolling ? 'Processing...' : 'Enroll Now'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 pb-24 lg:pb-0">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Calendar, label: 'Duration', value: `${course?.duration_weeks || Math.ceil((course?.duration_months || 3) * 4)} Weeks` },
                { icon: PlayCircle, label: 'Lessons', value: `${totalVideos} Videos` },
                { icon: Clock, label: 'Total Hours', value: `${Math.round(totalMinutes / 60)}+ Hours` },
                { icon: Award, label: 'Certificate', value: 'Included' },
              ].map((stat, i) => (
                <Card key={i} className={cardClass}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <stat.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                      <p className="font-semibold">{stat.value}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* About This Course */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  About This Program
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {course?.description}
                </p>
                
                {/* Skills Covered */}
                {course?.skills?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Skills You&apos;ll Master:</h4>
                    <div className="flex flex-wrap gap-2">
                      {course.skills.map((skill, i) => (
                        <Badge key={i} variant="secondary" className="px-3 py-1">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* What You'll Learn */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  What You&apos;ll Learn
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {getHighlights().map((item, i) => (
                    <motion.div 
                      key={i} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/30"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Sample Video Preview */}
            {previewVideos.length > 0 && (
              <Card className={cardClass}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MonitorPlay className="w-5 h-5 text-primary" />
                    Preview Lessons
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {previewVideos.map((video, i) => (
                      <div key={i} className="relative rounded-lg overflow-hidden bg-muted aspect-video group cursor-pointer">
                        <img 
                          src={`https://img.youtube.com/vi/${video.youtube_id || 'dQw4w9WgXcQ'}/maxresdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Play className="w-6 h-6 text-white fill-white ml-1" />
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80">
                          <p className="text-white text-sm font-medium">{video.title}</p>
                          <p className="text-white/70 text-xs">{video.duration_minutes} min • Free Preview</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Course Content / Curriculum */}
            <Card className={cardClass}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Course Curriculum
                  </CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {course?.modules?.length || 0} modules • {totalVideos} lessons
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {course?.modules?.map((module, i) => (
                    <AccordionItem key={module.module_id || i} value={module.module_id || `m${i}`}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0 font-bold">
                            {i + 1}
                          </div>
                          <div>
                            <p className="font-medium">{module.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {module.videos?.length || 0} lessons • {module.videos?.reduce((a, v) => a + (v.duration_minutes || 0), 0)} min
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-13 space-y-2 ml-6">
                          {module.videos?.map((video, vi) => (
                            <div
                              key={vi}
                              className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Play className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm">{video.title}</span>
                                {vi < 2 && i === 0 && (
                                  <Badge variant="outline" className="text-xs">Preview</Badge>
                                )}
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {video.duration_minutes} min
                              </span>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            {/* Career Opportunities */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Career Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  After completing this program, you&apos;ll be prepared for roles such as:
                </p>
                <div className="flex flex-wrap gap-3">
                  {getCareerOpportunities().map((career, i) => (
                    <Badge key={i} variant="outline" className="px-4 py-2 text-sm">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      {career}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Student Reviews - Placeholder */}
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  Student Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: 'Priya S.', rating: 5, comment: 'Amazing course! The mentors are very supportive and the curriculum is well-structured.', date: '2 weeks ago' },
                    { name: 'Rahul M.', rating: 5, comment: 'Best investment in my creative career. Landed my dream job after completing this program.', date: '1 month ago' },
                    { name: 'Ananya K.', rating: 4, comment: 'Great content and practical projects. Highly recommend for beginners.', date: '1 month ago' },
                  ].map((review, i) => (
                    <div key={i} className="p-4 rounded-lg bg-muted/30">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback>{review.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{review.name}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[...Array(5)].map((_, j) => (
                                <Star key={j} className={`w-3 h-3 ${j < review.rating ? 'fill-yellow-500 text-yellow-500' : 'text-muted'}`} />
                              ))}
                            </div>
                            <span className="text-xs text-muted-foreground">{review.date}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Pricing Section */}
            {pricingTiers && (
              <Card className={`${cardClass} overflow-hidden`} id="pricing">
                <CardContent className="p-6 md:p-8">
                  <CoursePricing 
                    courseId={courseId}
                    courseTitle={course?.title}
                    pricingTiers={pricingTiers}
                    onEnroll={handleEnroll}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block space-y-6">
            {/* Pricing Card - Sticky */}
            <Card className={`sticky top-24 ${cardClass} overflow-hidden`}>
              {/* Price Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 p-6 text-white">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-4xl font-bold flex items-center">
                    <IndianRupee className="w-8 h-8" />
                    {(course?.price || 0).toLocaleString('en-IN')}
                  </span>
                </div>
                <p className="text-white/80 text-sm">One-time payment • Lifetime access</p>
              </div>

              <CardContent className="p-6">
                <Button
                  className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 mb-4"
                  size="lg"
                  onClick={handleEnroll}
                  disabled={enrolling}
                  data-testid="enroll-button"
                >
                  {enrolling ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Enroll Now
                    </>
                  )}
                </Button>

                {!isAuthenticated && (
                  <p className="text-center text-sm text-muted-foreground mb-4">
                    <button 
                      onClick={() => navigate('/login/student')}
                      className="text-primary hover:underline"
                    >
                      Login
                    </button>
                    {' '}or{' '}
                    <button 
                      onClick={() => navigate('/login/student')}
                      className="text-primary hover:underline"
                    >
                      Sign up with Google
                    </button>
                    {' '}to enroll
                  </p>
                )}

                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
                  <Shield className="w-4 h-4" />
                  <span>Secure payment • 7-day refund policy</span>
                </div>

                {/* Course Includes */}
                <div className="space-y-3 border-t pt-4">
                  <h4 className="font-medium">This course includes:</h4>
                  {[
                    { icon: PlayCircle, text: `${totalVideos} video lessons` },
                    { icon: Clock, text: `${Math.round(totalMinutes / 60)}+ hours of content` },
                    { icon: Download, text: 'Downloadable resources' },
                    { icon: MessageCircle, text: 'Direct mentor support' },
                    { icon: Award, text: 'Certificate of completion' },
                    { icon: Users, text: 'Community access' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <item.icon className="w-4 h-4 text-primary" />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* Share & Wishlist */}
                <div className="flex gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" className="flex-1" size="sm">
                    <Heart className="w-4 h-4 mr-2" />
                    Wishlist
                  </Button>
                  <Button variant="outline" className="flex-1" size="sm">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Need Help Card */}
            <Card className={cardClass}>
              <CardContent className="p-6 text-center">
                <Palette className="w-10 h-10 mx-auto mb-3 text-primary" />
                <h4 className="font-medium mb-2">Need Help Choosing?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Talk to our counselors to find the right program for you.
                </p>
                <Button variant="outline" className="w-full" onClick={() => navigate('/contact')}>
                  Get Free Counseling
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Video Preview Modal */}
      {showVideoPreview && previewVideos[0] && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowVideoPreview(false)}
        >
          <div className="relative w-full max-w-4xl aspect-video">
            <button 
              onClick={() => setShowVideoPreview(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              Close ✕
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${previewVideos[0].youtube_id || 'dQw4w9WgXcQ'}?autoplay=1`}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
